<script setup generic="T" lang="ts">
import type { CheckboxSize, Radio } from '#imports';

interface IProps {
    name: string;
    radios?: Radio<T>[];
    disabled?: boolean;
    hasDefault?: boolean;
    row?: boolean | 'mobile';
    size?: CheckboxSize;
    boldLabel?: boolean;
    gapItems?: number;
    gapItemsMobile?: number;
    loading?: boolean;
    wrapLabel?: boolean;
    comparator?: (v1: T, v2: T) => boolean;
}
const {
    radios = [],
    disabled,
    hasDefault,
    row,
    size = 'medium',
    gapItems = 0,
    gapItemsMobile = 0,
    loading,
    comparator = (v1: T, v2: T) => v1 === v2,
} = defineProps<IProps>();

const emit = defineEmits<{
    blur: [];
}>();

const modelValue = defineModel<T | null>({ default: null });

const onSelect = (value: T | null | undefined) => {
    if (value !== null && value !== undefined) {
        modelValue.value = value;
    } else {
        modelValue.value = null;
    }
};
onBeforeMount(() => {
    if (hasDefault && radios.length > 0) {
        modelValue.value = radios[0].value;
    }
});
</script>

<template>
    <div
        class="mr-radio-button-root"
        :style="{
            '--gap': `${gapItems}px`,
            '--gap-m': `${gapItemsMobile || gapItems}px`,
        }"
        :class="{
            'mr-row': row === true,
            'row-in-desktop': row === 'mobile',
            'loading-skeleton': loading,
        }"
        @blur="emit('blur')"
    >
        <mr-checkbox
            v-for="(radio, index) in radios"
            :key="radio.value?.toString() || index"
            :disabled="disabled || radio.disabled || loading"
            :model-value="comparator(modelValue as T, radio.value as T)"
            :label="radio.label"
            :description="radio.description"
            :radio-name="name"
            :size
            :bold-label
            :href="radio.href"
            class="mr-checkbox"
            :wrap-label
            :class="[
                {
                    active: modelValue === radio.value,
                },
                radio.className,
            ]"
            :loading
            is-radio
            @update:model-value="() => onSelect(radio.value)"
        >
            <template #label="{ label }">
                <slot name="radio-label" :label :radio>
                    {{ label }}
                </slot>
            </template>
        </mr-checkbox>
    </div>
</template>
<style lang="scss" scoped>
.mr-radio-button-root {
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    @include mobile-only {
        gap: var(--gap-m);
    }
    &.mr-row {
        flex-direction: row;
        flex-wrap: wrap;
    }
    &.row-in-desktop {
        @include desktop-only {
            flex-direction: row;
        }
    }
    .mr-checkbox {
        padding-inline: 8px;
    }
}
</style>
